type LogContext = Record<string, unknown>;

interface ApmInstance {
	captureError(error: Error, opts?: { custom?: LogContext }): void;
	addLabels(labels: Record<string, unknown>): void;
}

interface LogProvider {
	error(error: Error | string, context?: LogContext): void;
	warn(message: string, context?: LogContext): void;
	debug(message: string, context?: LogContext): void;
}

function getCallerInfo(error?: Error | string): string | undefined {
	const stack =
		error instanceof Error
			? (error.stack?.split("\n") ?? [])
			: (new Error().stack
					?.split("\n")
					.filter(
						(line) =>
							!line.includes("log.ts") && !line.includes("log.js")
					) ?? []);
	const frame = stack.find((line) => line.includes(" at "));
	// "    at fnName (file:line:col)" or "    at file:line:col"
	const match =
		frame?.match(/\((.+?:\d+:\d+)\)/) ?? frame?.match(/at\s+(.+?:\d+:\d+)/);
	return match?.[1];
}

const consoleProvider: LogProvider = {
	error(error, context) {
		const caller = getCallerInfo(error);
		console.error(caller ? `[${caller}]` : "", error, context);
	},
	warn(message, context) {
		const caller = getCallerInfo();
		console.warn(caller ? `[${caller}]` : "", message, context);
	},
	debug(message, context) {
		const caller = getCallerInfo();
		console.debug(caller ? `[${caller}]` : "", message, context);
	},
};

function makeApmProvider(apm: ApmInstance): LogProvider {
	return {
		error(error, context) {
			const caller = getCallerInfo(error);
			console.error(caller ? `[${caller}]` : "", error, context);
			apm.captureError(
				error instanceof Error ? error : new Error(error),
				{
					custom: { ...context, ...(caller && { caller }) },
				}
			);
		},
		warn(message, context) {
			const caller = getCallerInfo();
			console.warn(caller ? `[${caller}]` : "", message, context);
			apm.addLabels({ warning: message, ...context });
		},
		debug(message, context) {
			const caller = getCallerInfo();
			console.debug(caller ? `[${caller}]` : "", message, context);
		},
	};
}

let provider: LogProvider = consoleProvider;

const log = {
	init(apm: ApmInstance) {
		provider = makeApmProvider(apm);
	},
	error(error: Error | string, context?: LogContext) {
		provider.error(error, context);
	},
	warn(message: string, context?: LogContext) {
		provider.warn(message, context);
	},
	debug(message: string, context?: LogContext) {
		provider.debug(message, context);
	},
	setProvider(p: LogProvider) {
		provider = p;
	},
};

export default log;
